/**
 * The design system, in one place (SCOPE §2.1 — decided 2026-08-02).
 *
 * These values are the canonical source and are mirrored into CSS custom
 * properties in `css/tokens.css` beside them, which both the register and the
 * web shop load (decision 140). Keeping them here in TypeScript is what lets
 * `contrast.test.ts` *enforce* WCAG AA on every text pairing the product
 * actually uses, rather than asserting it in a document nobody re-checks.
 *
 * `design-preview.html` in the repo root remains the living visual reference.
 */

export const COLORS = {
  /** Near-white app background: comfortable for an 8-hour shift, no glare. */
  bg: '#f8fafc',
  surface: '#ffffff',
  /** Slate, not pure black — the contrast is ample without being harsh. */
  text: '#0f172a',
  textSecondary: '#475569',
  border: '#e2e8f0',
  borderStrong: '#94a3b8',

  /** Alpine Blue. Reserved for the one primary action on a screen. */
  primary: '#1d4ed8',
  primaryHover: '#1e40af',
  /**
   * Two more shades of the same hue, for **identity and orientation only**.
   *
   * `primaryDeep` carries the accent rail across the top of the app and the
   * rule under the grand total. `primarySoft` tints the active nav tab — a
   * quieter blue than `infoBg`, so "you are here" does not read as an
   * informational callout. Neither ever says what to do or what went wrong,
   * which is what keeps §2.1's rule intact: colour means status or action.
   */
  primaryDeep: '#1e3a8a',
  primarySoft: '#eff6ff',

  /** Fixed semantic palette, independent of any future brand colour. */
  success: '#15803d',
  successBg: '#dcfce7',
  danger: '#b91c1c',
  dangerBg: '#fee2e2',
  warning: '#b45309',
  warningBg: '#fef3c7',
  info: '#1d4ed8',
  infoBg: '#dbeafe',
  neutralBg: '#f1f5f9',
} as const;

export type ColorToken = keyof typeof COLORS;

/**
 * Type scale for the register (SCOPE §2.1).
 *
 * Hierarchy comes from size and weight only. There is no third typeface and no
 * colour-coded text — colour means action or status, never emphasis.
 */
export const TYPE_SCALE = {
  /** Secondary text and field labels. */
  small: 14,
  /** Body copy and cart lines. */
  body: 16,
  /** Line prices. */
  price: 20,
  /** The grand total — always the largest element on the register. */
  total: 32,
  /** All-caps badges and column headers only. Never data. */
  micro: 11,
} as const;

export const WEIGHTS = { regular: 400, medium: 500, semibold: 600 } as const;

/** Minimum touch target, for a counter environment (SCOPE §2.1). */
export const MIN_TOUCH_TARGET_PX = 44;

/**
 * Category tints (SCOPE §2.1).
 *
 * §2.1 reserves the semantic palette — red destructive, green success, amber
 * warning — and says colour is never decoration. A categorical palette looks
 * like a violation of that until you ask what the colour is *doing*: these
 * appear only on the categories screen and the import assignment step, they
 * **always carry the category's name beside them**, and they never appear on a
 * button or a status badge. The colour is a second cue for a grouping the text
 * already states, which is what makes scanning a hundred rows possible at all.
 *
 * Pure red and pure green are excluded regardless, because those two carry the
 * most semantic load in this product and a refund is not a thing to be
 * ambiguous about.
 *
 * A category keeps its tint everywhere, derived from its id, so the tile on
 * this screen and the row it tints on an import are recognisably one thing
 * (§2.2-4, consistency).
 */
export const CATEGORY_TINTS: ReadonlyArray<{ readonly bg: string; readonly fg: string }> = [
  { bg: '#dbeafe', fg: '#1e3a8a' },
  { bg: '#e0e7ff', fg: '#312e81' },
  { bg: '#ede9fe', fg: '#4c1d95' },
  { bg: '#fae8ff', fg: '#701a75' },
  { bg: '#fce7f3', fg: '#831843' },
  { bg: '#cffafe', fg: '#164e63' },
  { bg: '#ccfbf1', fg: '#115e59' },
  { bg: '#ffedd5', fg: '#7c2d12' },
];

/**
 * Every text-on-background pairing the UI uses.
 *
 * Adding a colour combination to the product means adding it here, which means
 * the contrast test either passes it or fails the build. That is the whole
 * mechanism — §2.1 says contrast is "verified, not assumed".
 */
export const TEXT_PAIRINGS: ReadonlyArray<{
  readonly name: string;
  readonly foreground: string;
  readonly background: string;
  /** Large text (>= 24px or >= 19px bold) may use the 3:1 threshold. */
  readonly large?: boolean;
}> = [
  { name: 'body text on app background', foreground: COLORS.text, background: COLORS.bg },
  { name: 'body text on surface', foreground: COLORS.text, background: COLORS.surface },
  {
    name: 'secondary text on surface',
    foreground: COLORS.textSecondary,
    background: COLORS.surface,
  },
  {
    name: 'secondary text on app background',
    foreground: COLORS.textSecondary,
    background: COLORS.bg,
  },
  { name: 'white on primary button', foreground: COLORS.surface, background: COLORS.primary },
  { name: 'white on primary hover', foreground: COLORS.surface, background: COLORS.primaryHover },
  { name: 'primary as link on surface', foreground: COLORS.primary, background: COLORS.surface },
  { name: 'primary as link on app background', foreground: COLORS.primary, background: COLORS.bg },
  // The active nav tab: brand text on the soft brand tint.
  {
    name: 'active tab label on its tint',
    foreground: COLORS.primary,
    background: COLORS.primarySoft,
  },
  { name: 'white on the accent rail', foreground: COLORS.surface, background: COLORS.primaryDeep },
  { name: 'danger text on surface', foreground: COLORS.danger, background: COLORS.surface },
  // Warning as text rather than a badge: an out-of-stock count in search, and a
  // negative margin on a price the buyer is typing.
  { name: 'warning text on surface', foreground: COLORS.warning, background: COLORS.surface },
  { name: 'success badge', foreground: COLORS.success, background: COLORS.successBg },
  { name: 'danger badge', foreground: COLORS.danger, background: COLORS.dangerBg },
  { name: 'warning badge', foreground: COLORS.warning, background: COLORS.warningBg },
  { name: 'info badge', foreground: COLORS.info, background: COLORS.infoBg },
  { name: 'neutral badge', foreground: COLORS.textSecondary, background: COLORS.neutralBg },
  // Every category tint, so adding an unreadable one breaks the build rather
  // than shipping a chip nobody can read at arm's length.
  ...CATEGORY_TINTS.map((tint, index) => ({
    name: `category tint ${index + 1}`,
    foreground: tint.fg,
    background: tint.bg,
  })),
  // Tinted panels that carry ordinary prose rather than a status word: customer
  // tags and the notes block sit on the neutral tint, and a selected or hovered
  // search result sits on the informational one.
  { name: 'body text on neutral tint', foreground: COLORS.text, background: COLORS.neutralBg },
  { name: 'body text on informational tint', foreground: COLORS.text, background: COLORS.infoBg },
  // Written as #fff until 2026-09-25, when the invariant check sent them to the
  // tokens — and so into this test, which had never seen them: the count
  // column's all-counted tick, and the tooltip.
  { name: 'white on success', foreground: COLORS.surface, background: COLORS.success },
  { name: 'white on body text (tooltip)', foreground: COLORS.surface, background: COLORS.text },
];
